import React, { useState, useEffect, useRef } from 'react';
import { Menu, Bell, Search, User, Home, BookOpen, Calendar, BarChart3, Settings, Activity, LogOut, ChevronDown, MoreHorizontal, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuth } from '../../contexts/AuthContext';
import { LanguageSwitcher } from './LanguageSwitcher';

interface HeaderProps {
  onMenuToggle: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const allNavigationItems = [
    { name: t('navigation.dashboard'), href: '/', icon: Home },
    { name: t('navigation.lessons'), href: '/lessons', icon: BookOpen },
    { name: t('navigation.lessonLibrary'), href: '/lessons/library', icon: BookOpen },
    { name: t('navigation.students'), href: '/students', icon: Users },
    // { name: t('session.activeSession'), href: '/session', icon: Play },
    { name: t('navigation.schedule'), href: '/schedule', icon: Calendar },
    { name: t('tracking.dashboard'), href: '/tracking', icon: Activity },
    { name: t('navigation.analytics'), href: '/analytics', icon: BarChart3 },
    { name: t('navigation.settings'), href: '/settings', icon: Settings },
    // Admin navigation item - only show for admin users
    ...(user?.isAdmin ? [{ name: t('navigation.admin'), href: '/admin/lessons', icon: Settings }] : []),
  ];

  // Show first 4 items in main navigation
  const mainNavigationItems = allNavigationItems.slice(0, 4);
  // Show remaining items in dropdown
  const dropdownNavigationItems = allNavigationItems.slice(4);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
      <div className="flex items-center justify-between px-3 py-2 lg:px-4">
        {/* Left side */}
        <div className="flex items-center space-x-3">
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors bg-gray-50 border border-gray-200"
          >
            <Menu className="w-5 h-5 text-gray-700" />
          </button>
          
          {/* App Title - Always visible */}
          <h1 className="text-base font-semibold text-gray-900 lg:hidden">{t('header.appTitle')}</h1>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {mainNavigationItems.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 group ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 border border-primary-200'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${
                    isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'
                  }`} />
                  <span className="font-medium text-sm">{item.name}</span>
                </Link>
              );
            })}

            {/* More Menu Dropdown */}
            {dropdownNavigationItems.length > 0 && (
              <div className="relative" ref={moreMenuRef}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 group ${
                    dropdownNavigationItems.some(item => location.pathname === item.href)
                      ? 'bg-primary-50 text-primary-700 border border-primary-200'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <MoreHorizontal className={`w-4 h-4 ${
                    dropdownNavigationItems.some(item => location.pathname === item.href) 
                      ? 'text-primary-600' 
                      : 'text-gray-400 group-hover:text-gray-600'
                  }`} />
                  <span className="font-medium text-sm">{t('navigation.more')}</span>
                  <ChevronDown className="w-3 h-3 text-gray-400" />
                </motion.button>

                <AnimatePresence>
                  {showMoreMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                    >
                      {dropdownNavigationItems.map((item) => {
                        const isActive = location.pathname === item.href;
                        const Icon = item.icon;
                        
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setShowMoreMenu(false)}
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActive
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <Icon className={`w-4 h-4 ${
                              isActive ? 'text-primary-600' : 'text-gray-400'
                            }`} />
                            <span>{item.name}</span>
                          </Link>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </nav>
          
          {/* Search bar */}
          <div className="hidden md:flex items-center space-x-2 bg-gray-100 rounded-lg px-2 py-1.5 flex-1 max-w-md">
            <Search className="w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder={t('header.searchLessons')}
              className="bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-500 flex-1"
            />
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-2">
          {/* Language Switcher */}
          <div className="block">
            <LanguageSwitcher />
          </div>

          {/* Notifications */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Bell className="w-4 h-4 text-gray-600" />
            <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full"></span>
          </motion.button>

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
                <User className="w-3.5 h-3.5 text-primary-600" />
              </div>
              <span className="hidden sm:block text-sm font-medium text-gray-700">
                {user?.name || t('header.teacherName')}
              </span>
              <ChevronDown className="w-3 h-3 text-gray-400" />
            </motion.button>

            <AnimatePresence>
              {showUserMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 max-w-[calc(100vw-2rem)]"
                >
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate" title={user?.name}>{user?.name}</p>
                    <p className="text-xs text-gray-500 truncate break-all" title={user?.email}>{user?.email}</p>
                    <p className="text-xs text-gray-500 truncate break-all" title={user?.isAdmin ? 'Admin' : 'User'}>{user?.isAdmin ? 'Admin' : 'User'}</p> 
                  </div>
                  <button
                    onClick={signOut}
                    className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{t('auth.signOut')}</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Mobile search */}
      <div className="md:hidden px-3 pb-2">
        <div className="flex items-center space-x-2 bg-gray-100 rounded-lg px-2 py-1.5">
          <Search className="w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder={t('header.searchLessons')}
            className="bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-500 flex-1"
          />
        </div>
      </div>
    </header>
  );
};
